import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/ui-kit";
import { Seo } from "@/components/Seo";
import { captureLead, formatError } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setLoading(true);
    setError("");
    try {
      await captureLead({
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        source_page: "contact",
        tags: ["Contact"],
      });
      track.contact("contact");
      setSent(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact" description="Questions about the book, speaking, or the Builder Movement? Send a note." path="/contact" />
      <section className="relative bg-black noise pt-40 pb-24 lg:pt-48 lg:pb-32 min-h-[80vh]">
        <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-10">
          <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
          <Reveal delay={90}>
            <h1 className="font-display text-5xl lg:text-6xl text-white mt-7 tracking-tight leading-[1.03]">
              Let’s talk.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="font-body text-lg text-white/55 mt-7 max-w-xl leading-relaxed">
              Questions about the book, speaking, or the Builder Movement? Send a note — we read
              every message.
            </p>
          </Reveal>

          {sent ? (
            <Reveal>
              <div
                data-testid="contact-success"
                className="mt-14 border border-[#D4AF37] p-12 text-center max-w-xl"
              >
                <div className="w-14 h-14 border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                  <Check className="text-[#D4AF37]" size={26} strokeWidth={3} />
                </div>
                <h2 className="font-display text-3xl text-white">Message received.</h2>
                <p className="font-body text-white/55 mt-4">
                  Thank you for reaching out. We’ll be in touch soon.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={220}>
              <form onSubmit={onSubmit} data-testid="contact-form" className="mt-14 max-w-xl space-y-6">
                <div className="flex flex-col gap-2">
                  <label className="font-body text-xs tracking-[0.2em] uppercase text-white/50">Name</label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={onChange("name")}
                    className="bg-black border border-white/25 text-white px-5 py-4 font-body focus:outline-none focus:border-[#D4AF37] transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="font-body text-xs tracking-[0.2em] uppercase text-white/50">Email</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    className="bg-black border border-white/25 text-white px-5 py-4 font-body focus:outline-none focus:border-[#D4AF37] transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="font-body text-xs tracking-[0.2em] uppercase text-white/50">Message</label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={onChange("message")}
                    rows={5}
                    className="bg-black border border-white/25 text-white px-5 py-4 font-body focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                    placeholder="How can we help?"
                  />
                </div>
                {error && <p className="font-body text-sm text-red-400" data-testid="contact-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium px-9 py-4 transition-colors duration-300 disabled:opacity-60"
                >
                  <Mail size={17} /> {loading ? "Sending…" : "Send Message"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
